import React from 'react';
import {
  Inbox, Send, FileText, Star, AlertTriangle, Clock, ShieldAlert, Trash2, PenSquare, Settings,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';

const FOLDER_ICONS = {
  inbox: Inbox,
  sent: Send,
  drafts: FileText,
  starred: Star,
  important: AlertTriangle,
  outbox: Clock,
  spam: ShieldAlert,
  trash: Trash2,
};

const FOLDER_LABELS = {
  inbox: 'Inbox',
  sent: 'Sent',
  drafts: 'Drafts',
  starred: 'Starred',
  important: 'Important',
  outbox: 'Outbox',
  spam: 'Spam',
  trash: 'Trash',
};

function formatLastSync(iso) {
  if (!iso) return 'Never synced';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Never synced';
  return d.toLocaleString();
}

export default function CrmMailSidebar({
  folder,
  unreadCounts,
  onSelectFolder,
  onCompose,
  onSync,
  syncing,
  lastSyncAt,
  lastSyncError,
  canConfigure = false,
  onConfigure,
  fullWidth = false,
}) {
  return (
    <aside
      className={cn(
        'shrink-0 border-r border-border bg-card flex flex-col',
        fullWidth ? 'w-full' : 'w-full md:w-[220px]',
      )}
    >
      <div className="p-3 space-y-2">
        <Button
          onClick={onCompose}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          size="sm"
        >
          <PenSquare className="w-4 h-4 mr-2" /> Compose
        </Button>
        {canConfigure && onConfigure && (
          <Button variant="outline" size="sm" className="w-full" onClick={onConfigure}>
            <Settings className="w-4 h-4 mr-2" /> Mail settings
          </Button>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5">
        {Object.keys(FOLDER_LABELS).map((key) => {
          const Icon = FOLDER_ICONS[key];
          const count = unreadCounts?.[key] || 0;
          const showBadge = count > 0 && ['inbox', 'drafts', 'outbox'].includes(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelectFolder(key)}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                folder === key
                  ? 'bg-blue-600/10 text-blue-700 dark:text-blue-300 font-medium'
                  : 'text-foreground hover:bg-muted',
              )}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span className="flex-1 text-left">{FOLDER_LABELS[key]}</span>
              {showBadge && (
                <span className="text-xs font-semibold bg-blue-600 text-white rounded-full px-1.5 min-w-[1.25rem] text-center">
                  {count > 99 ? '99+' : count}
                </span>
              )}
            </button>
          );
        })}
      </nav>
      <div className="p-3 border-t space-y-2">
        <p className="text-xs text-muted-foreground px-1">
          {syncing ? 'Syncing inbox…' : `Last sync: ${formatLastSync(lastSyncAt)}`}
        </p>
        {lastSyncError && (
          <p className="text-xs text-destructive px-1 line-clamp-3" title={lastSyncError}>
            {lastSyncError}
          </p>
        )}
        <Button variant="outline" size="sm" className="w-full" onClick={onSync} disabled={syncing}>
          {syncing ? 'Syncing…' : 'Sync now'}
        </Button>
      </div>
    </aside>
  );
}
